import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Navbar from "./Navbar";
import HeroSection from "./HeroSection";
import ServiceScroller from "./ServiceScroller";
import AboutRebuilders from "./AboutRebuilders";
import ConstructionTests from "./ConstructionTests";
import AboutCivilEngineering from "./AboutCivilEngineering";
import ServiceCards from "./ServiceCard";
import ClientsShowcase from "./ClientsShowcase";
import Footer from "./Footer";
import AboutRevaUniversity from "./AboutRevaUniversity";

import { FiMail } from "react-icons/fi";
import { Plus, ChevronRight, Beaker, Ruler, Layers, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function HomeIndex() {
  const location = useLocation();
  const navigate = useNavigate();

  const [showTestingMenu, setShowTestingMenu] = useState(false);
  const [showMobileTesting, setShowMobileTesting] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // New state for main FAB

  useEffect(() => {
    if (location.state?.scrollTo) {
      const section = document.getElementById(location.state.scrollTo);
      if (section) {
        window.scrollTo({
          top: section.offsetTop - 80,
          behavior: "smooth",
        });
      }
    }
  }, [location]);

  const handleMailClick = () => {
    window.location.href =
      "mailto:consultancy.civil@reva.edu.in?subject=Consultation Request";
  };

  /* ================= MENU DATA ================= */
  const menuItems = [
    {
      title: "Advanced Services",
      link: "/AdvancedSpecializedServicesSection",
      icon: <Plus size={18} />,
    },
    {
      title: "Design Consultancy",
      link: "/DesignStructuralConsultancy",
      icon: <Layers size={18} />,
    },
    {
      title: "Surveying",
      link: "/Surveying",
      icon: <Ruler size={18} />,
    },
    {
      title: "Material Testing",
      link: "/GeneralMaterialTesting",
      icon: <Beaker size={18} />,
      hasSubmenu: true,
    },
  ];

  const testingSubMenu = [
    { title: "Aggregate Testing", link: "/AggregateTestingSection" },
    { title: "Cement Testing", link: "/CementTestingSection" },
    { title: "Concrete Testing", link: "/ConcreteTestingSection" },
    { title: "Soil Testing", link: "/SoilTestingSection" },
    { title: "Bitumen Testing", link: "/BitumenTestingSection" },
    { title: "Bricks, Blocks, Tiles & Pavers Testing", link: "/BricksBlocksTilesPaversTestingSection" },
    { title: "Steel Testing", link: "/SteelTestingSection" },
    { title: "Water & Wastewater Testing", link: "/WaterWastewaterTestingSection" },
  ];

  // Helper to handle navigation from the FAB
  const handleMenuClick = (item) => {
    if (item.hasSubmenu) {
      setShowTestingMenu(true);
      setIsMenuOpen(false); 
    } else {
      navigate(item.link);
      setIsMenuOpen(false);
    }
  };

  return (
    <div style={{ fontFamily: "Poppins, sans-serif" }} className="relative">
      <Navbar />

      {/* ================= NEW CONSOLIDATED FLOATING BUTTON ================= */}
      <div className="fixed bottom-8 right-8 z-[60] flex flex-col items-end gap-4">
        
        {/* Expanded Menu Items */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-end gap-3 mb-2"
            >
              {menuItems.map((item, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleMenuClick(item)}
                  whileHover={{ scale: 1.05, x: -5 }}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="
                    group flex items-center gap-3 pl-4 pr-2 py-2
                    bg-white text-gray-800
                    rounded-full shadow-lg border border-gray-100
                    hover:border-[#F37021] hover:text-[#F37021]
                    transition-colors
                  "
                >
                  <span className="text-sm font-medium whitespace-nowrap">
                    {item.title}
                  </span>
                  <div className="
                    w-10 h-10 rounded-full 
                    bg-gray-100 group-hover:bg-[#F37021] 
                    group-hover:text-white text-gray-600
                    flex items-center justify-center
                    transition-colors
                  ">
                    {item.icon}
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Buttons Container */}
        <div className="flex items-center gap-4">
            {/* Mail Button */}
            <motion.button
                onClick={handleMailClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="
                w-14 h-14 rounded-full
                bg-white text-[#F37021]
                shadow-lg border border-gray-100
                flex items-center justify-center
                "
                title="Send Email"
            >
                <FiMail size={24} />
            </motion.button>

            {/* The Main Menu Fab */}
            <motion.button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`
                w-14 h-14 rounded-full shadow-2xl
                flex items-center justify-center
                text-white transition-all duration-300
                ${isMenuOpen ? "bg-gray-800 rotate-90" : "bg-[#F37021]"}
                `}
            >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
        </div>
      </div>

      {/* ================= SIDE DRAWER FOR MATERIAL TESTING ================= */}
      <AnimatePresence>
        {showTestingMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowTestingMenu(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[65]"
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="
                fixed top-0 right-0 h-full
                w-[85vw] md:w-[400px]
                bg-[#121212] border-l border-[#F37021]/30
                shadow-2xl z-[70] flex flex-col
              "
            >
              <div className="p-6 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-b border-[#F37021]/20 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Material Testing
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Select a category below
                  </p>
                </div>
                <button 
                  onClick={() => setShowTestingMenu(false)}
                  className="p-2 hover:bg-white/10 rounded-full text-white transition"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {testingSubMenu.map((item, i) => (
                  <motion.button
                    key={i}
                    whileHover={{ x: 4, backgroundColor: "rgba(243, 112, 33, 0.1)" }}
                    onClick={() => {
                        setShowTestingMenu(false);
                        navigate(item.link);
                    }}
                    className="
                      w-full p-4 text-left rounded-lg
                      border border-white/5
                      hover:border-[#F37021]/40
                      text-gray-300 hover:text-white
                      flex justify-between items-center
                      transition-all
                    "
                  >
                    <span className="font-medium">{item.title}</span>
                    <ChevronRight size={16} className="text-[#F37021]" />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ================= PAGE CONTENT ================= */}
      <HeroSection />
      <ServiceCards />
      <ServiceScroller />
      <AboutRebuilders />

      <section id="projects-section">
        <ConstructionTests />
      </section>

      <section id="about-section">
        <AboutCivilEngineering />
      </section>

      <ClientsShowcase />
      <Footer />
    </div>
  );
}

export default HomeIndex;